import { createBomError, sortBomVersions } from '../utils/bom-version.utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import APIService from '../../../services/api.service';
import { DEFAULT_QUERY } from '../../items/state/table-data.state';
import { Item } from '../../items/types/item.types';
import { Query } from '../../../shared/types/query.types';
import { ToastService } from '../../../services/toast.service';
import { convertBackendItemToFrontendItem } from '../../../types/item.mapper';
import { formatBackendDateForDisplay } from '../../../shared/utils/fiscal-year.utils';
import { useBomVersionState } from '../state/bom-version.state';
import { useTableDataState } from '../../items/state/table-data.state';

export type { BomError } from '../utils/bom-version.utils';

export interface BomVersionsHookResult {
   versions: string[];
   currentVersion: string | null;
   isLoadingVersions: boolean;
   isLoadingVersionChange: boolean;
   isCloning: boolean;
   error: import('../utils/bom-version.utils').BomError | null;
   switchVersion: (bomValidFrom: string) => void;
   clearVersion: () => void;
   cloneVersion: (bomValidFrom: string) => Promise<boolean>;
   fetchVersions: () => void;
   clearError: () => void;
}

interface CloneResult {
   success: boolean;
   bomValidFrom: string;
}

interface VersionChangeResult {
   item: Item;
   bomValidFrom: string;
}

export function useBomVersions(itemNo?: string): BomVersionsHookResult {
   const queryClient = useQueryClient();
   const [manualError, setManualError] = useState<import('../utils/bom-version.utils').BomError | null>(null);
   const { getItemBomVersion, setItemBomVersion, clearItemBomVersion } = useBomVersionState();
   const { updateItemInTable } = useTableDataState();

   const currentBomVersion = itemNo ? getItemBomVersion(itemNo) : null;

   useEffect(() => {
      setManualError(null);
   }, [itemNo]);

   const handleMutationError = useCallback((error: unknown, context: string) => {
      const formattedError = createBomError(error);
      setManualError(formattedError);
      ToastService.error(`${context}: ${formattedError.message}`);
   }, []);

   const clearError = useCallback(() => {
      setManualError(null);
   }, []);

   const {
      data: versions = [],
      isLoading: isLoadingVersions,
      error: versionsError,
      refetch: refetchVersions,
   } = useQuery(['bomVersions', itemNo], () => APIService.getBomValidFromDates(itemNo || ''), {
      enabled: false,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: (data) => {
         const valid = (data || []).filter((v): v is string => v !== null && v !== undefined && typeof v === 'string');
         return sortBomVersions(valid);
      },
      onError: (error) => handleMutationError(error, 'Failed to load BOM versions'),
   });

   const fetchVersions = useCallback(() => {
      if (!itemNo) return;
      const cached = queryClient.getQueryData(['bomVersions', itemNo]);
      if (!cached) refetchVersions();
   }, [itemNo, queryClient, refetchVersions]);

   const updateVersion = useCallback(
      (itemNo: string, bomValidFrom: string | null) => {
         if (bomValidFrom === null || bomValidFrom === 'default') {
            clearItemBomVersion(itemNo);
         } else {
            setItemBomVersion(itemNo, bomValidFrom);
         }
         queryClient.invalidateQueries(['data']);
      },
      [clearItemBomVersion, setItemBomVersion, queryClient]
   );

   const { mutate: switchVersionMutate, isLoading: isLoadingSwitchVersion } = useMutation<VersionChangeResult | null, unknown, string>(
      async (bomValidFrom: string) => {
         if (!itemNo) return null;
         if (currentBomVersion === bomValidFrom) return null;

         ToastService.info(`Loading BOM version: ${formatBackendDateForDisplay(bomValidFrom)}`);

         const filterQuery: Query = {
            ...DEFAULT_QUERY,
            itemNos: [itemNo],
            bomValidFrom: bomValidFrom,
            pageSize: 1,
            offset: 0,
            progress: false,
         };

         const response = await APIService.getItems(filterQuery);
         if (!response.items?.length) throw new Error('Failed to load BOM version data');

         const itemWithVersion = convertBackendItemToFrontendItem(response.items[0]);
         itemWithVersion.bomValidFrom = bomValidFrom;

         return { item: itemWithVersion, bomValidFrom };
      },
      {
         onSuccess: (data) => {
            if (data && itemNo) {
               updateVersion(itemNo, data.bomValidFrom);
               updateItemInTable(itemNo, data.item);
               ToastService.success(`Switched to BOM version: ${formatBackendDateForDisplay(data.bomValidFrom)}`);
            }
         },
         onError: (error) => handleMutationError(error, 'Failed to load BOM version'),
      }
   );

   const { mutate: clearVersionMutate, isLoading: isLoadingClearVersion } = useMutation<Item | null, unknown, void>(
      async () => {
         if (!itemNo) return null;

         ToastService.info('Returning to default view...');

         const filterQuery: Query = {
            ...DEFAULT_QUERY,
            itemNos: [itemNo],
            pageSize: 1,
            offset: 0,
            progress: false,
         };

         const response = await APIService.getItems(filterQuery);
         if (!response.items?.length) throw new Error('Failed to load default view');

         return convertBackendItemToFrontendItem(response.items[0]);
      },
      {
         onSuccess: (item) => {
            if (item && itemNo) {
               updateVersion(itemNo, null);
               updateItemInTable(itemNo, item);
               ToastService.success('Returned to default view');
            }
         },
         onError: (error) => handleMutationError(error, 'Failed to return to default view'),
      }
   );

   const { mutateAsync: cloneVersionMutate, isLoading: isCloning } = useMutation<CloneResult, unknown, string>(
      async (bomValidFrom: string) => {
         if (!itemNo) throw new Error('Item number is required');

         const success = await APIService.cloneBom(itemNo, bomValidFrom);
         if (!success) throw new Error('Server returned false for clone operation');

         return { success: true, bomValidFrom };
      },
      {
         onSuccess: async (data) => {
            ToastService.success('BOM cloned successfully');
            await queryClient.invalidateQueries(['bomVersions', itemNo]);
            await refetchVersions();
            switchVersionMutate(data.bomValidFrom);
         },
         onError: (error) => handleMutationError(error, 'Failed to clone BOM'),
      }
   );

   const isLoadingVersionChange = isLoadingSwitchVersion || isLoadingClearVersion;

   const error = useMemo(() => {
      if (manualError) return manualError;
      return versionsError ? createBomError(versionsError) : null;
   }, [manualError, versionsError]);

   const switchVersion = useCallback((bomValidFrom: string) => switchVersionMutate(bomValidFrom), [switchVersionMutate]);

   const clearVersion = useCallback(() => clearVersionMutate(), [clearVersionMutate]);

   const cloneVersion = useCallback(
      async (bomValidFrom: string): Promise<boolean> => {
         try {
            await cloneVersionMutate(bomValidFrom);
            return true;
         } catch {
            return false;
         }
      },
      [cloneVersionMutate]
   );

   return {
      versions,
      currentVersion: currentBomVersion,
      isLoadingVersions,
      isLoadingVersionChange,
      isCloning,
      error,
      switchVersion,
      clearVersion,
      cloneVersion,
      fetchVersions,
      clearError,
   };
}
